import { useState, useCallback, useEffect, useRef } from 'react';
import { usePipeline } from '../../context/PipelineContext';
import { mapSFOpportunities } from '../../utils/sfMapper';
import { getEffectiveProbability } from '../../utils/pipelineCalculations';
import { apiFetch } from '../../utils/apiFetch';

const AUTO_SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const LAST_SYNC_KEY = 'bcc_sf_last_sync';

// Push effective probability for SF-synced prospects back to SF so the
// dashboard's smart-decayed + manually-overridden probabilities win over
// whatever SF has stored.
//
// Volume control (Jun 2026): this used to PATCH every prospect with an
// sfId on EVERY sync — including the ~200+ opportunities closed in 2026
// that the SOQL pulls for outcome stats. With the 5-minute auto-sync,
// that produced a continuous ~1 request/second flood to /api/sf/update
// (thousands of SF API calls a day, drowning the Vercel logs and causing
// intermittent 500s under load). Two filters fix it:
//   1. Skip closed prospects. SF locks Probability to 100/0 on close
//      automatically; pushing it is a no-op at best, a validation error
//      at worst.
//   2. Skip prospects whose effective probability already matches SF's
//      stored Probability (pulled in the sync SOQL as sfProbability).
//      Decay only moves the rounded percentage occasionally, so a
//      typical auto-sync now pushes zero to a handful of updates.
const CLOSED_STAGES = new Set(['closed-won', 'closed-lost', 'unqualified']);

async function pushEffectiveProbabilitiesToSF(prospects) {
  const candidates = prospects.filter(p => p.sfId && !CLOSED_STAGES.has(p.stage));
  let pushed = 0, skipped = 0;
  for (const p of candidates) {
    const { probability } = getEffectiveProbability(p);
    const sfProbability = Math.round(probability * 100);
    if (p.sfProbability != null && p.sfProbability === sfProbability) {
      skipped++;
      continue;
    }
    try {
      await apiFetch('/api/sf/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sfId: p.sfId,
          fields: { Probability: sfProbability },
        }),
      });
      pushed++;
    } catch (err) {
      console.warn(`Failed to push probability for ${p.name}:`, err);
    }
  }
  if (pushed > 0 || skipped > 0) {
    console.log(`[SF] probability push: ${pushed} updated, ${skipped} unchanged, ${prospects.length - candidates.length} closed/non-SF skipped`);
  }
}

export default function SFSyncButton() {
  const { mergeFromSalesforce } = usePipeline();
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const autoSyncedRef = useRef(false);

  async function checkConnection() {
    try {
      const res = await apiFetch('/api/sf/sync?type=status');
      const data = await res.json();
      return data.success && data.data?.connected;
    } catch {
      return false;
    }
  }

  const sync = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setSyncing(true);
    setStatus(null);
    if (!silent) setMessage('Checking Salesforce connection...');

    const connected = await checkConnection();
    if (!connected) {
      if (silent) { setSyncing(false); return; } // Don't auto-redirect silently
      setMessage('Connecting to Salesforce...');
      window.location.href = '/api/sf/connect';
      return;
    }

    try {
      if (!silent) setMessage('Pulling opportunities...');
      const res = await apiFetch('/api/sf/sync?type=opportunities');
      const data = await res.json();

      if (!data.success) throw new Error(data.error || 'Sync failed');

      const records = data.data?.records || [];
      const prospects = mapSFOpportunities(records);
      mergeFromSalesforce(prospects);

      try { localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString()); } catch {}

      // Push our effective probability back to SF so the dashboard always
      // overrides whatever's in SF (smart decay + manual overrides).
      // Fire-and-forget; errors logged in console.
      pushEffectiveProbabilitiesToSF(prospects);

      setStatus('success');
      setMessage(`Synced ${prospects.length} opportunities`);
      if (!silent) setTimeout(() => window.location.reload(), 1500);
      else setTimeout(() => { setStatus(null); setMessage(''); }, 3000);
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    } finally {
      setSyncing(false);
    }
  }, [mergeFromSalesforce]);

  // Auto-sync on mount if it's been more than 5 minutes since last sync
  useEffect(() => {
    if (autoSyncedRef.current) return;
    autoSyncedRef.current = true;
    try {
      const last = localStorage.getItem(LAST_SYNC_KEY);
      const stale = !last || (Date.now() - new Date(last).getTime() > AUTO_SYNC_INTERVAL_MS);
      if (stale) sync({ silent: true });
    } catch { /* ignore */ }
  }, [sync]);

  // Periodic auto-sync every 5 minutes while page is open
  useEffect(() => {
    const interval = setInterval(() => sync({ silent: true }), AUTO_SYNC_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [sync]);

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={sync}
        disabled={syncing}
        className={`px-3 py-1.5 text-[11px] font-bold rounded-[4px] transition-colors ${
          syncing
            ? 'bg-white/10 text-white/50 cursor-wait'
            : status === 'success'
              ? 'bg-deep-teal text-white'
              : status === 'error'
                ? 'bg-rose text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white border border-white/20'
        }`}
        title="Sync prospects from Salesforce"
      >
        {syncing ? message : status === 'success' ? 'Synced!' : status === 'error' ? 'Error' : 'Sync SF'}
      </button>
      {status === 'error' && (
        <span className="text-[10px] text-rose max-w-[200px] truncate">{message}</span>
      )}
    </div>
  );
}
